import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useLoadPaginatedFrontend } from '../../app/hooks/useLoadPaginatedFrontend';
import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Tooltip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { SearchTextField, Title } from '../../components';

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    margin-bottom: 20px;

    h1 {
        font-size: 28px;
        margin: 0;
    }

    h2 {
        font-size: 18px;
        opacity: 0.6;
        letter-spacing: 2px;
        margin: 0;
    }
`;

const Wrapper = styled.div`
    width: calc(100% - 2 * 24px);
    display: flex;
    flex-direction: column;
    padding: 24px;
    align-items: stretch;
`;

const States = () => {
  const history = useHistory();
  const user = useSelector(state => state.user);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const limitedStateIds = (user.limitAccessToStates || []).map(state => state._id || state);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const query = {
    type: 'state',
    $sort: { name: 1 },
    ...(limitedStateIds.length ? { _id: limitedStateIds } : {}),
    ...(debouncedSearchTerm.trim() ? { searchTerm: { $search: debouncedSearchTerm.trim() } } : {})
  };

  const districts = useLoadPaginatedFrontend(
    'districts',
    query,
    [debouncedSearchTerm, JSON.stringify(limitedStateIds)],
    50,
  );

  const loading = districts?.entity === null || districts?.entity === undefined;
  const states = loading
    ? []
    : (districts.entity || []).sort((a, b) => a.name.localeCompare(b.name));

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleRowClick = (matchName) => {
    history.push(`/states/${matchName}`);
  };

  return (
    <Wrapper>
      <Title>States</Title>
      <Row>
        <h1>States</h1>
        <SearchTextField
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          inputRef={searchInputRef}
          InputProps={{
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <ClearIcon 
                  style={{ cursor: 'pointer' }}
                  onClick={handleClearSearch}
                />
              </InputAdornment>
            ),
          }}
        />
      </Row>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  State Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  Election Config
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={40} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : states.length > 0 ? (
              states.map((state) => (
                <TableRow
                  key={state.matchName}
                  onClick={() => handleRowClick(state.matchName)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body1">
                      {state.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip 
                      title={state.electionConfigurationComplete ? 'Election configuration complete' : 'Election configuration incomplete'}
                      placement="top"
                    >
                      <Box display="flex" alignItems="center">
                        {state.electionConfigurationComplete ? (
                          <CheckCircleIcon 
                            fontSize="small" 
                            style={{ color: '#4caf50' }} 
                          />
                        ) : (
                          <CancelIcon 
                            fontSize="small" 
                            style={{ color: '#9e9e9e' }} 
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <Typography variant="body2" color="textSecondary">
                      {debouncedSearchTerm ? 'No states found matching your search.' : 'No states found.'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Wrapper>
  );
};

export default States;
